import { useState, useEffect, useRef, useCallback } from 'react';
import { notificationsApi } from '../api/notifications.js';

const POLL_INTERVAL_MS = 60_000;

/**
 * Polls /api/notifications every 60 s.
 * Returns { notifications, unreadCount, markRead, markAllRead, refetch }.
 */
export function useNotifications(user) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  const fetch_ = useCallback(async () => {
    if (!user) return;
    try {
      const data = await notificationsApi.list();
      setNotifications(data ?? []);
    } catch {
      // Fail silently — badge just stays stale
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setNotifications([]);
      return;
    }
    setLoading(true);
    fetch_().finally(() => setLoading(false));

    timerRef.current = setInterval(fetch_, POLL_INTERVAL_MS);
    return () => clearInterval(timerRef.current);
  }, [user, fetch_]);

  const markRead = useCallback(async (id) => {
    await notificationsApi.markRead(id);
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  }, []);

  const markAllRead = useCallback(async () => {
    await notificationsApi.markAllRead();
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  const unreadCount = notifications.filter((n) => !n.read).length;

  return {
    notifications,
    unreadCount,
    loading,
    markRead,
    markAllRead,
    refetch: fetch_,
  };
}
